/* LaunchFlow: homepage module carousels — custom arrow behavior.

   Two things this takes over from Dawn's native SliderComponent
   (assets/global.js), scoped only to our own carousels:

   1. Arrow disable state. Dawn disables "previous" with a rock-solid check
      (scrollLeft === 0) but disables "next" purely from comparing
      fractional-pixel element geometry against the viewport edge. On a
      real screen (non-round widths, zoom, device pixel ratio) that
      geometry check can land a hair off and never fire, leaving the right
      arrow visible even once the carousel is fully scrolled to its end.

   2. Click-to-scroll distance and animation. Each arrow press advances the
      carousel by exactly SLIDES_PER_PRESS products at a single constant
      speed (linear, no easing curve). Neither is achievable with the native
      smooth scroll, which has a fixed non-configurable duration and an
      ease-in-out curve.

   Manual dragging/swiping/trackpad scrolling is untouched either way —
   this only changes what happens when an arrow is clicked. We don't edit
   global.js itself (it's shared by every slider in the theme); instead we
   replace just our two buttons with clones (dropping Dawn's own listeners
   in the process) and attach our own click handling to those. */
(function () {
  if (window.__lfCarouselNavInit) return;
  window.__lfCarouselNavInit = true;

  // One press moves exactly this many products.
  var SLIDES_PER_PRESS = 5;

  // Constant pixels-per-millisecond, so the motion runs at ONE speed from
  // the first frame to the last. Duration is derived from the distance
  // rather than fixed, which is what keeps the speed uniform whatever the
  // slide width or viewport. Clamped so a short final hop to the end still
  // feels deliberate and a long one never drags.
  var SCROLL_SPEED_PX_PER_MS = 2.6;
  var MIN_DURATION_MS = 220;
  var MAX_DURATION_MS = 620;

  // Linear — no easing curve at all. The previous easeInOutCubic is exactly
  // what produced the "pause, crawl, then accelerate away" feel: a cubic
  // ease-in-out spends its first and last third moving very slowly and
  // sprints through the middle.
  function linear(t) {
    return t;
  }

  function wireUpTrack(track) {
    var wrap = track.closest('slider-component');
    if (!wrap) return;
    var prevBtn = wrap.querySelector('.lf-nav--prev');
    var nextBtn = wrap.querySelector('.lf-nav--next');
    if (!prevBtn || !nextBtn) return;

    // Swap in clones so Dawn's own (fixed-speed) click listeners — already
    // attached by the time this deferred script runs — don't fire too.
    // Attributes (data-step, aria-label, disabled, etc.) carry over with
    // the clone; only event listeners are dropped.
    var freshPrev = prevBtn.cloneNode(true);
    var freshNext = nextBtn.cloneNode(true);
    prevBtn.replaceWith(freshPrev);
    nextBtn.replaceWith(freshNext);
    prevBtn = freshPrev;
    nextBtn = freshNext;

    // We're driving scrollLeft manually frame-by-frame below; CSS
    // scroll-behavior: smooth would additionally try to smooth those very
    // assignments, fighting our own easing. Turn it off for this element —
    // manual drag/swipe/trackpad scrolling is unaffected either way, since
    // scroll-behavior only governs programmatic scroll changes.
    track.style.scrollBehavior = 'auto';

    // Width of one product plus the gap between products, measured from the
    // live DOM rather than hardcoded, so it stays correct across the two
    // carousels (which use different slide widths) and at every viewport.
    function slideStride() {
      var slides = track.children;
      if (slides.length < 1) return track.clientWidth;
      var first = slides[0].getBoundingClientRect();
      if (slides.length >= 2) {
        // Distance between two consecutive slide starts already includes the
        // flex gap, so there is no need to parse it separately.
        return slides[1].getBoundingClientRect().left - first.left;
      }
      return first.width;
    }

    var animationFrameId = null;
    function animateTo(target) {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      var start = track.scrollLeft;
      var maxScroll = track.scrollWidth - track.clientWidth;
      // Never overshoot either end.
      target = Math.max(0, Math.min(target, maxScroll));
      var change = target - start;
      if (Math.abs(change) < 1) return;
      var duration = Math.min(
        MAX_DURATION_MS,
        Math.max(MIN_DURATION_MS, Math.abs(change) / SCROLL_SPEED_PX_PER_MS)
      );
      var startTime = null;
      // CSS scroll-snap (mandatory, on this track) fights a manual
      // frame-by-frame scrollLeft animation — the browser tries to pull
      // each intermediate frame to the nearest snap point, so instead of
      // gliding it visibly steps between slides. Suspend snapping only for
      // the duration of the animation, then hand back to the browser once
      // we land exactly on our (already snap-valid) target.
      track.style.scrollSnapType = 'none';
      function step(timestamp) {
        if (startTime === null) startTime = timestamp;
        var progress = Math.min((timestamp - startTime) / duration, 1);
        track.scrollLeft = start + change * linear(progress);
        if (progress < 1) {
          animationFrameId = requestAnimationFrame(step);
        } else {
          animationFrameId = null;
          track.style.scrollSnapType = '';
        }
      }
      animationFrameId = requestAnimationFrame(step);
    }

    // One press = SLIDES_PER_PRESS products, in either direction. Previously
    // these jumped straight to scroll position 0 / the very end, which is
    // what made a single click sweep the entire carousel.
    prevBtn.addEventListener('click', function (event) {
      event.preventDefault();
      animateTo(track.scrollLeft - slideStride() * SLIDES_PER_PRESS);
    });
    nextBtn.addEventListener('click', function (event) {
      event.preventDefault();
      animateTo(track.scrollLeft + slideStride() * SLIDES_PER_PRESS);
    });

    function refresh() {
      var maxScroll = track.scrollWidth - track.clientWidth;
      var atStart = track.scrollLeft <= 1;
      var atEnd = maxScroll <= 1 || track.scrollLeft >= maxScroll - 1;
      prevBtn.toggleAttribute('disabled', atStart);
      nextBtn.toggleAttribute('disabled', atEnd);
    }

    track.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh);
    // Late-loading images can very slightly shift layout after first paint;
    // re-check once everything has settled.
    window.addEventListener('load', refresh);
    refresh();
  }

  document.querySelectorAll('.lf-pl-track, .lf-bs-track').forEach(wireUpTrack);
})();
